import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import useToggle from '_hooks/useToggle';
import {
  HOST_ORDER_CANCELED,
  HOST_ORDER_COLLECTED,
  HOST_ORDER_UNPAID,
  HOST_ORDER_VERIFIED,
  HOST_ORDER_SUBMITTED_UNPAID,
  HOST_ORDER_SUBMITTED_VERIFIED,
  HOST_ORDER_SUBMITTED_COLLECTED,
} from '_utils/constant';
import { formatNumberEng } from '_utils/formatNumber';
import '../style.scss';

const NoAddressBadge = () => (
  <div className="booking-address-missing">No address — assign</div>
);

function TablePackageList({ listOrder, isByAdmin, onChangeSelect }) {
  const [expandedRows, toggleDetails] = useToggle({});

  const groupedOrdersMap = useMemo(() => {
    if (!listOrder) return {};
    const map = {};
    listOrder.forEach((order) => {
      const orders = order.orders || [];
      const events = order.events || [];

      const zipped = orders.map((item, index) => ({
        item,
        event: events[index] || null,
      }));

      const groups = {};
      zipped.forEach((zippedItem) => {
        const addrKey = (zippedItem.item.bookingAddress || '').trim();
        if (!groups[addrKey]) {
          groups[addrKey] = [];
        }
        groups[addrKey].push(zippedItem);
      });

      const result = [];
      const visitedKeys = new Set();
      zipped.forEach((zippedItem) => {
        const addrKey = (zippedItem.item.bookingAddress || '').trim();
        if (!visitedKeys.has(addrKey)) {
          visitedKeys.add(addrKey);
          const group = groups[addrKey];
          group.forEach((itemInGroup, idx) => {
            result.push({
              ...itemInGroup,
              isFirstInGroup: idx === 0,
              groupSize: group.length,
            });
          });
        }
      });
      map[order.uid] = result;
    });
    return map;
  }, [listOrder]);

  const renderInformationAddress = useCallback((order) => {
    const orders = order.orders || [];
    const orderCount = orders.length;
    const isExpanded = !!expandedRows[order.uid];

    if (orderCount === 1) {
      const address = orders[0]?.bookingAddress;
      return address ? (
        <div className="booking-address-text">{address}</div>
      ) : (
        <NoAddressBadge />
      );
    }

    if (orderCount >= 2) {
      const hasAnyAddress = orders.some((item) => !!item.bookingAddress);
      if (!hasAnyAddress) {
        return <NoAddressBadge />;
      }

      const allAddresses = orders.map((item) => item.bookingAddress || '');
      const uniqueAddresses = [...new Set(allAddresses)];

      if (uniqueAddresses.length > 1) {
        return (
          <button
            type="button"
            className="show-details-btn"
            onClick={() => toggleDetails(order.uid)}
            style={{ marginTop: 0 }}
          >
            {isExpanded ? (
              <>
                <FaAngleUp size={10} />
                Hide address list
              </>
            ) : (
              <>
                <FaAngleDown size={10} />
                Show address list
              </>
            )}
          </button>
        );
      }

      const sharedAddress = orders.find((item) => !!item.bookingAddress)?.bookingAddress;
      return <div className="booking-address-text">{sharedAddress}</div>;
    }

    return <NoAddressBadge />;
  }, [expandedRows, toggleDetails]);

  if (!listOrder || listOrder.length === 0) {
    return (
      <div className="order-not-found">
        Order Not Found
      </div>
    );
  }

  return (
    <div className="packing-list-new-table">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ paddingRight: 0 }}>Contact</th>
              <th>Orders</th>
              <th>Address</th>
              <th>Total Cost</th>
              <th>Status</th>
            </tr>
          </thead>
          {listOrder.map((order) => {
            const isExpanded = !!expandedRows[order.uid];
            const totalItems = order.allProductPerOrder
              ? order.allProductPerOrder.reduce((acc, pro) => acc + pro.pQuantity, 0)
              : 0;
            const totalEvents = order.orders ? order.orders.length : 0;

            let statusClass = 'status-none';
            if (totalEvents > 0 && order.statusPaid === totalEvents) {
              statusClass = 'status-full';
            } else if (order.statusPaid > 0) {
              statusClass = 'status-part';
            }

            const totalOrderCost = formatNumberEng(order.totalFeeAllOrder, 2);
            const itemsCost = formatNumberEng(order.totalPricePerOrder, 2);
            const deliveryCost = formatNumberEng(order.totalDeliveryPerOrder, 2);
            const adminCost = formatNumberEng(order.adminCostPerOrder, 2);
            const discountCost = formatNumberEng(order.totalDiscountPerOrder, 2);

            return (
              <tbody
                key={order.uid}
                className="buyer-group"
              >
                <tr className="buyer-row">
                  <td style={{ paddingRight: 0 }}>
                    <div className="buyer-name">{order.uName}</div>
                    <div className="buyer-phone">{order.uPhone}</div>
                  </td>
                  <td>
                    <div className="order-summary">
                      {totalItems} items · {totalEvents} events
                    </div>
                    <button
                      type="button"
                      className="show-details-btn"
                      onClick={() => toggleDetails(order.uid)}
                    >
                      {isExpanded ? (
                        <>
                          <FaAngleUp size={10} />
                          Hide details
                        </>
                      ) : (
                        <>
                          <FaAngleDown size={10} />
                          Show details
                        </>
                      )}
                    </button>
                  </td>
                  <td style={{ minWidth: '180px' }}>
                    {renderInformationAddress(order)}
                  </td>
                  <td className="col-cost-cell">
                    <div className="total-fee">
                      ${totalOrderCost}
                    </div>
                    <div className="cost-breakdown">
                      Items: ${itemsCost}<br />
                      Delivery: ${deliveryCost}<br />
                      Admin: ${adminCost}<br />
                      <span style={{ color: '#E24B4A' }}>
                        Disc: -${discountCost}
                      </span>
                    </div>
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <span className={`status-badge ${statusClass}`}>
                      Paid {order.statusPaid} / {totalEvents}
                    </span>
                  </td>
                </tr>

                {isExpanded &&
                  (groupedOrdersMap[order.uid] || []).map(
                    ({ item, event, isFirstInGroup, groupSize }, i) => {
                      const eventTitle = event?.title || 'Unknown Event';
                      const productsInEvent =
                        order.allProductPerOrder?.filter(
                          (product) => product.ref === item.ref
                        ) || [];

                      return (
                        <tr className="detail-row" key={`${order.uid}-${item.ref || i}`}>
                          <td style={{ padding: 0 }}></td>
                          <td className='col-order-detail'>
                            <div className="detail-event-group" style={{ margin: 0 }}>
                              <div className="detail-event-name" style={{ margin: 0 }}>
                                {eventTitle}
                              </div>
                              {productsInEvent.map((product) => (
                                <div className="detail-item-row" key={product.pid}>
                                  <span className="product-name">{product.pName}</span>
                                  <span className="product-quantity">
                                    x{product.pQuantity}
                                  </span>
                                  <span className="product-price">
                                    {item.discountPerOrder > 0 ? (
                                      <>
                                        <span className='discount-number'>
                                          ${formatNumberEng(product.pPrice * product.pQuantity, 2)}
                                        </span>
                                        <span style={{ fontWeight: 600, color: 'black' }}>
                                          ${formatNumberEng(item.totalFee, 2)}
                                        </span>
                                      </>
                                    ) : (
                                      `$${formatNumberEng(product.pPrice * product.pQuantity, 2)}`
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                          {isFirstInGroup && (
                            <td
                              className="detail-address"
                              rowSpan={groupSize}
                              style={{ verticalAlign: 'top' }}
                            >
                              {item.bookingAddress || <p className="no-address-dash">_</p>}
                            </td>
                          )}
                          <td style={{ padding: 0 }}></td>
                          <td className="col-payment-status">
                            <select
                              aria-label="Payment status"
                              className={`form-control-payment-cus ${isByAdmin ? 'disabled' : ''} ${
                                [
                                  HOST_ORDER_UNPAID,
                                  HOST_ORDER_SUBMITTED_UNPAID,
                                ].includes(item.status)
                                  ? 'status-color-unpaid'
                                  : [
                                      HOST_ORDER_VERIFIED,
                                      HOST_ORDER_SUBMITTED_VERIFIED,
                                      HOST_ORDER_SUBMITTED_COLLECTED,
                                    ].includes(item.status)
                                  ? 'status-color-verified'
                                  : 'status-color-other'
                              }`}
                              value={item.status}
                              disabled={isByAdmin}
                              name='status'
                              onChange={(e) =>
                                onChangeSelect(
                                  e,
                                  item.ref,
                                  item.status,
                                  item.buyerStatus,
                                  item.combinedPaymentRef
                                )
                              }
                            >
                              <option value={HOST_ORDER_CANCELED}>Cancelled</option>
                              <option value={HOST_ORDER_UNPAID}>Unpaid</option>
                              <option value={HOST_ORDER_SUBMITTED_UNPAID}>
                                Submitted & Unpaid
                              </option>
                              <option value={HOST_ORDER_SUBMITTED_VERIFIED}>
                                Submitted & Verified
                              </option>
                              <option value={HOST_ORDER_SUBMITTED_COLLECTED}>
                                Submitted & Collected
                              </option>
                              <option value={HOST_ORDER_VERIFIED}>Verified</option>
                              <option value={HOST_ORDER_COLLECTED}>Collected</option>
                            </select>
                          </td>
                        </tr>
                      );
                    }
                  )}
              </tbody>
            );
          })}
        </table>
      </div>
    </div>
  );
}

TablePackageList.propTypes = {
  listOrder: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string.isRequired,
      uName: PropTypes.string,
      uPhone: PropTypes.string,
      totalFeeAllOrder: PropTypes.number,
      totalPricePerOrder: PropTypes.number,
      totalDeliveryPerOrder: PropTypes.number,
      adminCostPerOrder: PropTypes.number,
      totalDiscountPerOrder: PropTypes.number,
      statusPaid: PropTypes.number,
      allProductPerOrder: PropTypes.array,
      orders: PropTypes.array,
      events: PropTypes.array,
    })
  ),
  isByAdmin: PropTypes.bool,
  onChangeSelect: PropTypes.func.isRequired,
};

export default React.memo(TablePackageList);
